// Global auth middleware. Runs on every route (SSR + client) and is
// also the single place we populate useAuth().me before layouts/pages
// look at roles. Without this, layouts that depend on `hasRole(...)`
// during setup (e.g. layouts/default.vue deciding whether to fire the
// /api/ui/households fetch for the header household picker) would see
// an empty roles array on SSR — even after the cookie is valid — and
// gate the fetch off, leaving the picker empty on hard refresh of any
// route that doesn't sit under a /admin parent (which separately runs
// role-admin-read and would have loaded roles for us).
//
// We piggyback on the same /api/auth/me call instead of adding a
// second round-trip.
export default defineNuxtRouteMiddleware(async (to) => {
  // No-session pages: reachable without authentication. The login-flow pages
  // (login/otp/verify-email/forgot/reset) must be public or this middleware
  // would bounce an unauthenticated visitor straight back to the IdP and the
  // custom login could never render.
  const publicPaths = new Set<string>([
    '/forbidden', '/signed-out',
    '/login', '/otp', '/verify-email', '/forgot-password', '/create-new-password'
  ])
  // ignore Nuxt internals + your API routes
  if (to.path.startsWith('/api/')) return
  if (publicPaths.has(to.path)) return
  // The /setup/{token} flow is for brand-new users who have not yet
  // chosen a password — they MUST be able to reach the page without
  // an existing session. The token in the URL is the authorization.
  if (to.path.startsWith('/setup/')) return

  const { me, loaded } = useAuth()

  // If a prior middleware/plugin already populated useAuth state
  // (e.g. role-admin-read on /admin/* routes), don't refetch — but
  // still honour the authenticated check.
  if (!loaded.value) {
    const headers = process.server ? useRequestHeaders(['cookie']) : undefined
    const { data } = await useFetch<{ authenticated: boolean; roles: string[]; claims: any | null }>(
      '/api/auth/me',
      { headers, credentials: 'include', key: 'auth.me.global' }
    )
    me.value = data.value || { authenticated: false, roles: [], claims: null }
    loaded.value = true
  }

  if (!me.value.authenticated) {
    // Preserve the user's intended destination through the sign-in round-trip.
    const returnTo = encodeURIComponent(to.fullPath)
    const cfg = useRuntimeConfig()
    if (cfg.public.nativeAuthLoginEnabled) {
      // Custom in-app login (Native Auth) is the default — internal nav to the
      // public /login page (no IdP redirect). MUST be `return`ed, not just
      // awaited: an internal navigateTo only aborts the in-progress route when
      // it's the middleware's return value. Merely awaiting it lets the
      // protected page mount anyway (layout fires its data calls → 401 flood)
      // while the soft-nav races. The external branch below hard-redirects via
      // window.location, so it wins either way — which is why this bug only
      // surfaced once the custom-login flag turned the internal branch on.
      return navigateTo(`/login?returnTo=${returnTo}`)
    } else {
      // Fallback: the Microsoft-hosted OIDC redirect login.
      return navigateTo(`/api/auth/login?returnTo=${returnTo}`, { external: true })
    }
  }
})
